// Taşeron portalı — Belge Analizi gelen kutusu: 1. seviye (analiz) satırının `⋯` menüsü
// (plan S25 C4, `docs/plans/S25-analysis-inbox.md`). Üç bölüm: yüklenen dosyaların
// manifestosu (salt-okur), `Arşivle`/`Arşivden çıkar` (§20.6, anında — onaysız), `Analizi sil`
// (§20.5, `ConfirmDialog` arkasında, danger ton).
//
// **`Arşivle` ↔ `Arşivden çıkar` (S25 web kod incelemesi #4).** `archived` prop'u satırın HANGİ
// rayda render EDİLDİĞİNİ taşır (`AnalysisInboxRail`'in `analyses`/`archivedAnalyses`
// listelerinden HANGİSİ — `entry.analysis.archivedAt` DEĞİL, o yalnız MANUEL arşivin zaman
// damgasıdır ve *etkin arşiv*in türetilmiş ikinci koluyla (§20.3) uyuşmayabilir). Eskiden
// `Arşivle` KOŞULSUZ sunuluyordu — zaten arşivdeki bir satırda da — ve `unarchiveDocumentAnalysis`
// (`packages/shared`) hiçbir çağrı noktası taşımıyordu: bekleyen kalemi olan bir analiz
// arşivlenince rayın/`PendingAnalysesStrip`/tepsinin HİÇBİRİNDEN bir daha geri dönüş yolu
// yoktu. İki mutasyon AYRI TUTULUR (aynı anda ikisi de mount edilmez, `archived`e göre biri
// render edilir) — §20.6 ikisi de durumsuz/idempotent olsa da UI tarafında "hangisi" HER ZAMAN
// satırın kendi bulunduğu bölümden bellidir, ayrıca bir toggle DURUMU TUTULMAZ.
//
// **`Analizi sil` → `409 DOCUMENT_ANALYSIS_HAS_ACCEPTED_ITEMS` (D4/A8).** Onaylanmış belge
// içeren bir analiz SİLİNEMEZ — onay kalıcı bir uygunluk kararıdır. Bu HATA değil, ÜRÜN
// KARARIdır: aynı diyalog "arşivlensin mi" adımına GEÇER (ikinci bir Vazgeç/Onayla turu, ayrı
// bir modal DEĞİL) ve onay `Arşivle`ye basınca `archiveDocumentAnalysis` çağrılır. `409
// DOCUMENT_ANALYSIS_NOT_TERMINAL` asla oluşamaz — `canDelete` menüyü zaten terminal olmayan
// (`Queued`/`Running`) analizlerde GİZLER (aynı `DocumentAnalysisResultPage.tsx` `canDiscard`
// deseni).
//
// Odak: `⋯` tetikleyicisi menü kapansa/diyalog açılsa da DOM'da KALIR (menü öğesinin aksine) —
// `useReturnFocus.ref` (sabit tek tetikleyici) burada DOĞRU araçtır, `.capture` DEĞİL (bu
// bileşenin her örneği zaten kendi tetikleyicisine sahip, satır başına DEĞİŞEN bir ref değil).

import {
  type ApiError,
  ConfirmDialog,
  type DocumentAnalysisDetailDto,
  type DocumentAnalysisStatus,
  Spinner,
  documentAnalysesApi,
  hasErrorCode,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supplierContractorKeys } from '../lib/supplierContractorKeys';
import { useReturnFocus } from '../lib/useReturnFocus';

const iconButtonClass =
  'shrink-0 p-1 text-muted hover:bg-surface-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

type DeleteStep = 'confirm' | 'blocked';

export function AnalysisInboxMenu({
  companyId,
  analysisUuid,
  status,
  detail,
  archived,
}: {
  companyId: number | null;
  analysisUuid: string;
  status: DocumentAnalysisStatus;
  /** Kaynak dosya listesi + kalem sayısı buradan gelir — henüz gelmediyse `undefined`
   * (dosya bölümü yükleniyor gösterir, `Analizi sil` sayısı BEKLER). */
  detail: DocumentAnalysisDetailDto | undefined;
  /** Bu satır rayın *etkin arşiv* bölümünde mi render ediliyor (`?status=archived`, §20.3) —
   * `entry.analysis.archivedAt` DEĞİL, çağıranın (`AnalysisInboxRail`) HANGİ listeden
   * geçtiğidir (dosya üstü not, S25 web kod incelemesi #4). */
  archived: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const menuTrigger = useReturnFocus<HTMLButtonElement>();

  // F2 (S25 web güvenlik incelemesi) — menü KONTROLLÜ tutulur ki `Arşivle` bir hata ile
  // sonuçlandığında menü KAPANMASIN (aksi hâlde hata hiçbir yerde görünmeden kaybolur, kuyruk
  // "temizlendi" sanılır). `DropdownMenu.Item`'ın `onSelect`'i `event.preventDefault()` ile
  // Radix'in varsayılan kapanışını iptal eder; başarıda `setMenuOpen(false)` KENDİMİZ kapatır.
  const [menuOpen, setMenuOpen] = useState(false);

  function invalidateAll() {
    for (const filter of ['awaiting-review', 'active', 'all', 'archived'] as const) {
      queryClient.invalidateQueries({
        queryKey: supplierContractorKeys.analyses(companyId, filter),
      });
    }
    queryClient.invalidateQueries({
      queryKey: supplierContractorKeys.analysis(companyId, analysisUuid),
    });
  }

  const archiveMutation = useMutation<
    Awaited<ReturnType<typeof documentAnalysesApi.archiveDocumentAnalysis>>,
    ApiError,
    void
  >({
    mutationFn: () => documentAnalysesApi.archiveDocumentAnalysis(analysisUuid),
    onSuccess: () => {
      invalidateAll();
      setMenuOpen(false);
    },
  });

  // #4 (S25 web kod incelemesi) — arşivin tersi. `archiveMutation`la AYNI F2 kablolaması
  // (hata varken menü AÇIK kalır) — yalnız uç ve etiket farklı.
  const unarchiveMutation = useMutation<
    Awaited<ReturnType<typeof documentAnalysesApi.unarchiveDocumentAnalysis>>,
    ApiError,
    void
  >({
    mutationFn: () => documentAnalysesApi.unarchiveDocumentAnalysis(analysisUuid),
    onSuccess: () => {
      invalidateAll();
      setMenuOpen(false);
    },
  });

  // Menü kapanırken (başarı hariç — o zaten `setMenuOpen(false)` çağırır) bir sonraki açılışta
  // eski hatanın görünmemesi için mutasyon durumu sıfırlanır.
  function handleMenuOpenChange(open: boolean) {
    setMenuOpen(open);
    if (open) {
      archiveMutation.reset();
      unarchiveMutation.reset();
    }
  }

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState<DeleteStep>('confirm');

  const discardMutation = useMutation<
    Awaited<ReturnType<typeof documentAnalysesApi.discardDocumentAnalysis>>,
    ApiError,
    void
  >({
    mutationFn: () => documentAnalysesApi.discardDocumentAnalysis(analysisUuid),
    onSuccess: () => {
      invalidateAll();
      closeDelete();
    },
    onError: (error) => {
      // Onaylanmış belge yüzünden reddedildi — bu bir hata METNİ DEĞİL, aynı diyalogda ikinci
      // bir adıma (arşivle) geçiş sinyalidir (D4).
      if (hasErrorCode(error, 'DOCUMENT_ANALYSIS_HAS_ACCEPTED_ITEMS')) {
        setDeleteStep('blocked');
      }
    },
  });

  // "Blocked" adımının kendi arşivleme çağrısı — menüdeki doğrudan `Arşivle` ile AYNI
  // mutasyonu paylaşır (idempotent, §20.6); adım burada da kapanışı tetikler.
  const blockedArchiveMutation = useMutation<
    Awaited<ReturnType<typeof documentAnalysesApi.archiveDocumentAnalysis>>,
    ApiError,
    void
  >({
    mutationFn: () => documentAnalysesApi.archiveDocumentAnalysis(analysisUuid),
    onSuccess: () => {
      invalidateAll();
      closeDelete();
    },
  });

  function closeDelete() {
    setDeleteOpen(false);
    setDeleteStep('confirm');
    menuTrigger.restore();
  }

  const canDelete = status === 'Succeeded' || status === 'Failed';
  const itemCount = detail?.items.length ?? 0;
  // F1 — silme onayı bu SAYIYA göre yeniden yazılır (bkz. `analysis.inbox.menu.deleteConfirmBody`
  // dosya üstü notu): `detail.sources.length`, ne kadar KAYNAK belgenin (kullanıcının kendi
  // yüklediği dosyalar) analizle birlikte kalıcı silineceğidir — `itemCount` (çıkarılan belge
  // sayısı) DEĞİL.
  const sourceCount = detail?.sources.length ?? 0;
  const genericDeleteError =
    discardMutation.isError &&
    !hasErrorCode(discardMutation.error, 'DOCUMENT_ANALYSIS_HAS_ACCEPTED_ITEMS')
      ? discardMutation.error
      : undefined;

  return (
    <>
      <DropdownMenu.Root open={menuOpen} onOpenChange={handleMenuOpenChange}>
        <DropdownMenu.Trigger asChild>
          <button
            ref={menuTrigger.ref}
            type="button"
            aria-label={t('analysis.inbox.menuLabel')}
            className={iconButtonClass}
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="z-[var(--z-dropdown)] min-w-[16rem] max-w-[22rem] overflow-hidden border border-border bg-surface p-1 shadow-md"
          >
            <div className="px-2 py-1.5 text-xs font-medium text-muted">
              {t('analysis.inbox.menu.filesHeading')}
            </div>
            {detail === undefined ? (
              <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted">
                <Spinner className="h-4 w-4" />
                {t('common.loading')}
              </div>
            ) : (
              detail.sources.map((source) => (
                <div key={source.documentUuid} className="flex items-baseline gap-2 px-2 py-1">
                  <span className="min-w-0 flex-1 truncate text-sm text-fg">{source.fileName}</span>
                  <span className="shrink-0 text-xs tnum text-muted">
                    {source.pageCount !== null
                      ? t('analysis.result.sourcePageCount', { count: source.pageCount })
                      : '—'}
                  </span>
                </div>
              ))
            )}
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
            {archived ? (
              <DropdownMenu.Item
                onSelect={(event) => {
                  // F2 — Radix'in varsayılan (senkron) kapanışını iptal eder: mutasyon
                  // sonuçlanana kadar menü AÇIK kalır ki bir hata görünür yerde kalsın;
                  // başarıda `unarchiveMutation.onSuccess` kendisi kapatır.
                  event.preventDefault();
                  unarchiveMutation.mutate();
                }}
                disabled={unarchiveMutation.isPending}
                className="flex cursor-pointer select-none items-center gap-2 px-2 py-1.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-muted data-[disabled]:cursor-default data-[disabled]:opacity-60"
              >
                {unarchiveMutation.isPending ? <Spinner className="h-4 w-4" /> : null}
                {t('analysis.inbox.menu.unarchive')}
              </DropdownMenu.Item>
            ) : (
              <DropdownMenu.Item
                onSelect={(event) => {
                  // F2 — Radix'in varsayılan (senkron) kapanışını iptal eder: mutasyon
                  // sonuçlanana kadar menü AÇIK kalır ki bir hata görünür yerde kalsın; başarıda
                  // `archiveMutation.onSuccess` kendisi kapatır.
                  event.preventDefault();
                  archiveMutation.mutate();
                }}
                disabled={archiveMutation.isPending}
                className="flex cursor-pointer select-none items-center gap-2 px-2 py-1.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-muted data-[disabled]:cursor-default data-[disabled]:opacity-60"
              >
                {archiveMutation.isPending ? <Spinner className="h-4 w-4" /> : null}
                {t('analysis.inbox.menu.archive')}
              </DropdownMenu.Item>
            )}
            {archived && unarchiveMutation.isError ? (
              <p role="alert" className="px-2 py-1.5 text-xs text-danger">
                {resolveApiErrorMessage(unarchiveMutation.error, t)}
              </p>
            ) : !archived && archiveMutation.isError ? (
              <p role="alert" className="px-2 py-1.5 text-xs text-danger">
                {resolveApiErrorMessage(archiveMutation.error, t)}
              </p>
            ) : null}
            {canDelete && detail !== undefined ? (
              <DropdownMenu.Item
                onSelect={() => setDeleteOpen(true)}
                className="flex cursor-pointer select-none items-center gap-2 px-2 py-1.5 text-sm text-danger outline-none data-[highlighted]:bg-danger-subtle"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t('analysis.inbox.menu.deleteAction', { count: itemCount })}
              </DropdownMenu.Item>
            ) : null}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          setDeleteOpen(open);
          if (!open) {
            closeDelete();
          }
        }}
        title={
          deleteStep === 'blocked'
            ? t('analysis.inbox.menu.deleteBlockedTitle')
            : t('analysis.inbox.menu.deleteConfirmTitle')
        }
        body={
          deleteStep === 'blocked'
            ? t('analysis.inbox.menu.deleteBlockedBody')
            : t('analysis.inbox.menu.deleteConfirmBody', { count: sourceCount })
        }
        confirmLabel={
          deleteStep === 'blocked'
            ? t('analysis.inbox.menu.archive')
            : t('analysis.inbox.menu.deleteConfirmTitle')
        }
        confirmVariant={deleteStep === 'blocked' ? 'primary' : 'danger'}
        loading={
          deleteStep === 'blocked' ? blockedArchiveMutation.isPending : discardMutation.isPending
        }
        error={
          deleteStep === 'blocked'
            ? blockedArchiveMutation.isError
              ? blockedArchiveMutation.error
              : undefined
            : genericDeleteError
        }
        errorFallback={
          deleteStep === 'blocked'
            ? 'analysis.inbox.menu.archiveFailed'
            : 'analysis.inbox.menu.deleteFailed'
        }
        onConfirm={() => {
          if (deleteStep === 'blocked') {
            blockedArchiveMutation.mutate();
          } else {
            discardMutation.mutate();
          }
        }}
      />
    </>
  );
}
